import { useStateManager, useStackSignature, useStateHistory, useLocation, useShared } from '../Services/StateManager.ts';
import { Request, type Method, type Body, type RequestOptions, type RequestError } from '../Http/Client/Request.ts';
import { ErrorModal } from '../Support/ErrorModal.ts';
import { CompleteResponse } from '../Http/Client/Response.ts';
import type { RouterRedirect } from '../Types/RouterRedirect.ts';
import { EventBus } from '../Events/EventBus.ts';
import { inject, type InjectionKey } from 'vue';
import { useUrlGenerator } from '../Services/UrlGenerator.ts';

interface HttpOptions extends Omit<RequestOptions, 'method' | 'url' | 'locale' | 'body' | 'signature' | 'referer'> {
  data?: Body | undefined;
  preserveScroll?: boolean | undefined;
  replace?: boolean | undefined;
}

export const HttpClientScrollHandler = Symbol('HttpClientScrollHandler') as InjectionKey<undefined | (() => void)>;
export const HttpClientForceScrollPreservation = Symbol('HttpClientForceScrollPreservation') as InjectionKey<boolean>;
export const HttpClientForceNested = Symbol('HttpClientForceNested') as InjectionKey<boolean>;

export function useHttpClient() {
  const state = useStateManager();
  const shared = useShared();
  const signature = useStackSignature();
  const history = useStateHistory();
  const location = useLocation();
  const url = useUrlGenerator();

  const scrollHandler = inject(HttpClientScrollHandler, () => document.body.scroll({ behavior: 'instant', left: 0, top: 0 }));
  const forceScrollPreserve = inject(HttpClientForceScrollPreservation, false);
  const forceNested = inject(HttpClientForceNested, false);

  async function dispatch(method: Method, url: string, { data = undefined, preserveScroll = false, replace = false, nested = false, ...options }: HttpOptions = {}) {
    document.body.classList.add('osf-loading');
    document.dispatchEvent(new Event('visit:start'));

    return await Request.send({
      ...options,
      method,
      url,
      locale: shared.value.appLocale,
      body: data,
      signature: signature.value,
      referer: location.value,
      nested: nested || forceNested,
    }).then(async (response: CompleteResponse) => {
      if (response.redirect) {
        let redirect = response.redirect;

        if (!redirect.reload) {
          return await state.update(response).then(async (): Promise<any> => {
            return await handleRedirectResponse(redirect);
          });
        }

        return await handleRedirectResponse(redirect);
      }

      return await state.update(response).then(async (fresh): Promise<any> => {
        if (response.raw) {
          return Promise.resolve(response.data);
        }

        if (!forceScrollPreserve && !preserveScroll) {
          resetScrollPosition();
        }

        if (replace) {
          history.historyReplaceState(fresh);
        } else {
          history.historyPushState(fresh);
        }

        return Promise.resolve(response);
      });
    }).catch(async (error: RequestError) => {
      let response = error.response;

      if (response !== null && response !== undefined) {
        if (response instanceof CompleteResponse) {
          return await state.update(response).then(() => Promise.reject(error));
        }

        if (response.status === 423) {
          EventBus.dispatch('password.confirm', { method, url, options: { data, preserveScroll, replace } });

          return Promise.reject(error);
        }

        if (shared.value.appDebug && response.content) {
          ErrorModal.show(response.content);
        }
      }

      console.error(error);

      return Promise.reject(error);
    }).finally(() => {
      document.body.classList.remove('osf-loading');
      document.dispatchEvent(new Event('visit:done'));
    });
  }

  function resetScrollPosition() {
    if (scrollHandler) {
      scrollHandler();
    }
  }

  async function handleRedirectResponse(redirect: RouterRedirect): Promise<any> {
    if (redirect.reload || url.isCrossOriginTarget(redirect.target)) {
      return await new Promise(() => {
        window.location.href = redirect.target;
      });
    }

    return await dispatch('GET', redirect.target, { preserveScroll: true, replace: false, refreshStack: true });
  }

  return {
    dispatch,

    get: async function (url: string) {
      return await dispatch('GET', url);
    },

    post: async function (url: string, data: Body | undefined = undefined) {
      return await dispatch('POST', url, { data, preserveScroll: true });
    },

    patch: async function (url: string, data: Body | undefined = undefined) {
      return await dispatch('PATCH', url, { data, preserveScroll: true });
    },

    put: async function (url: string, data: Body | undefined = undefined) {
      return await dispatch('PUT', url, { data, preserveScroll: true });
    },

    delete: async function (url: string, data: Body | undefined = undefined) {
      return await dispatch('DELETE', url, { data, preserveScroll: true });
    },
  };
}
